import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import '../styles/components/StructuredAdviceDisplay.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hashimoto-backend01.onrender.com';

// 症状に応じた寄り添いメッセージ（フロント固定）
const EMPATHY_MESSAGES = {
  fatigue: {
    message: '毎日おつかれさまです。全部やらなくて大丈夫。ひとつだけ、試してみませんか？',
    icon: '🌸'
  },
  cold: {
    message: '冷えがつらい日もありますよね。無理せず、温かくして過ごしてください。',
    icon: '🧣'
  },
  sleep_disorder: {
    message: '眠れない夜はつらいですよね。少しでも楽になるヒントをお伝えします。',
    icon: '🌙'
  },
  irritability: {
    message: 'イライラするのは、あなたのせいじゃないです。ホルモンの影響かもしれません。',
    icon: '🍃'
  },
  constipation: {
    message: 'おなかの調子が気になりますよね。できることから少しずつ。',
    icon: '🌿'
  },
  concentration: {
    message: '集中できないのはつらいですよね。甲状腺の影響かもしれません。',
    icon: '🌼'
  },
  default: {
    message: '忙しい中、相談してくれてありがとう。全部やらなくて大丈夫。',
    icon: '🌸'
  }
};

const StructuredAdviceDisplay = ({
  aiAdvice,
  consultationContent,
  basicInfo,
  labValues,
  doctorConsult,
  onNewConsultation,
  onContinueConsultation,
  consultationId
}) => {
  const [simpleMode, setSimpleMode] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  const [savedRecipes, setSavedRecipes] = useState(new Set());

  // 表示モード切り替え時にアコーディオン状態を更新
  useEffect(() => {
    if (simpleMode) {
      // かんたん表示: 最初の3セクションを開く
      setExpandedSections({ 0: true, 1: true, 2: true });
    } else {
      // 詳細表示: すべて開く
      const allOpen = {};
      for (let i = 0; i < 20; i++) allOpen[i] = true;
      setExpandedSections(allOpen);
    }
  }, [simpleMode]);

  // 寄り添いメッセージを取得
  const getEmpathyMessage = () => {
    if (!basicInfo?.symptoms) return EMPATHY_MESSAGES.default;

    const symptoms = basicInfo.symptoms;
    const priorityOrder = ['fatigue', 'sleep_disorder', 'cold', 'irritability', 'constipation', 'concentration'];

    // 配列の場合（userInput.symptoms = ['fatigue', 'cold', ...]）
    if (Array.isArray(symptoms)) {
      for (const symptom of priorityOrder) {
        if (symptoms.includes(symptom)) return EMPATHY_MESSAGES[symptom];
      }
    } else {
      // オブジェクトの場合（{fatigue: true, cold: true, ...}）
      for (const symptom of priorityOrder) {
        if (symptoms[symptom]) return EMPATHY_MESSAGES[symptom];
      }
    }
    return EMPATHY_MESSAGES.default;
  };

  // レシピを保存（API経由）
  const saveRecipe = async (blockIndex, section) => {
    try {
      const autoTags = [];
      const title = section.title.toLowerCase();
      if (title.includes('疲れ') || title.includes('🟢')) autoTags.push('簡単');
      if (title.includes('🟡')) autoTags.push('普通');
      if (title.includes('🔴') || title.includes('余裕')) autoTags.push('本格');
      if (title.includes('朝食')) autoTags.push('朝食');
      if (title.includes('昼食')) autoTags.push('昼食');
      if (title.includes('夕食')) autoTags.push('夕食');

      const response = await fetch(`${API_URL}/api/recipes/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: section.title,
          content: section.content,
          sourceConsultationId: consultationId,
          tags: autoTags
        })
      });

      const data = await response.json();
      if (data.success) {
        const newSavedRecipes = new Set(savedRecipes);
        newSavedRecipes.add(blockIndex);
        setSavedRecipes(newSavedRecipes);
        alert(data.isNew ? '✅ レシピを保存しました！\nマイページの「保存レシピ」からいつでも見られます' : 'このレシピは既に保存されています');
      } else {
        alert('❌ レシピの保存に失敗しました: ' + data.message);
      }
    } catch (error) {
      alert('❌ レシピの保存に失敗しました');
    }
  };

  // 英語症状名を日本語に変換
  const translateSymptomNames = (text) => {
    const symptomMap = {
      'fatigue': '疲労感', 'constipation': '便秘', 'sleep_disorder': '睡眠障害',
      'muscle_pain': '筋肉痛・関節痛', 'skin_dryness': '肌の乾燥', 'swelling': 'むくみ',
      'cold_sensitivity': '冷え性', 'hair_loss': '抜け毛', 'weight_gain': '体重増加',
      'concentration_difficulty': '集中力低下', 'depression': '気分の落ち込み',
      'irregular_menstruation': '生理不順'
    };
    let result = text;
    Object.entries(symptomMap).forEach(([en, ja]) => {
      result = result.replace(new RegExp(`\\b${en}\\b`, 'gi'), ja);
    });
    return result;
  };

  // セクション解析
  const parseAdviceIntoSections = (advice) => {
    if (!advice) return { sections: [], rawContent: advice };

    const translatedAdvice = translateSymptomNames(advice);
    const sections = [];
    const lines = translatedAdvice.split('\n');
    let currentSection = null;
    let currentContent = [];

    lines.forEach(line => {
      const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
      if (headerMatch) {
        if (currentSection && currentContent.length > 0) {
          const content = currentContent.join('\n').trim();
          if (content.length > 0) {
            sections.push({ ...currentSection, content });
          }
        }
        const rawTitle = headerMatch[1];
        // タイトル先頭の絵文字を除去（アイコンはsection.iconで表示するため）
        const cleanTitle = rawTitle.replace(/^[\u{1F300}-\u{1FAD6}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]+\s*/u, '').trim();
        const level = line.match(/^#{1,6}/)[0].length;
        currentSection = {
          title: translateSymptomNames(cleanTitle),
          level,
          type: detectSectionType(rawTitle),
          icon: getSectionIcon(rawTitle)
        };
        currentContent = [];
      } else {
        currentContent.push(line);
      }
    });

    if (currentSection && currentContent.length > 0) {
      const content = currentContent.join('\n').trim();
      if (content.length > 0) {
        sections.push({ ...currentSection, content });
      }
    }

    return {
      sections: sections.filter(s => {
        const stripped = s.content.replace(/[#*\-_`]/g, '').trim();
        return stripped.length > 0;
      }),
      rawContent: translatedAdvice
    };
  };

  // セクションタイプ検出（新構成対応）
  const detectSectionType = (title) => {
    const t = title.toLowerCase();

    // 新構成のセクション
    if (t.includes('今週のひとつだけ') || t.includes('📌')) return 'one_action';
    if (t.includes('安心して食べていい') || t.includes('✅')) return 'ok_list';
    if (t.includes('少しだけ気をつけ') || t.includes('💡')) return 'caution';
    if (t.includes('生活のヒント') || t.includes('🌿')) return 'lifestyle';

    // レシピ関連
    if (t.includes('レシピ') || t.includes('🍽') || t.includes('メニュー') ||
        t.includes('朝食') || t.includes('昼食') || t.includes('夕食') ||
        t.includes('疲れてる日') || t.includes('元気な日') || t.includes('余裕がある日') ||
        t.includes('🟢') || t.includes('🟡') || t.includes('🔴')) return 'recipe';

    // 旧構成との互換
    if (t.includes('サプリ') || t.includes('ビタミン')) return 'nutrition';
    if (t.includes('避け') || t.includes('制限')) return 'avoid';
    if (t.includes('生活') || t.includes('運動') || t.includes('睡眠')) return 'lifestyle';

    return 'general';
  };

  // セクションアイコン
  const getSectionIcon = (title) => {
    const type = detectSectionType(title);
    const icons = {
      one_action: '📌',
      ok_list: '✅',
      recipe: '🍽️',
      caution: '💡',
      lifestyle: '🌿',
      nutrition: '💊',
      avoid: '⚠️',
      general: '📝'
    };
    return icons[type] || '📝';
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const { sections, rawContent } = parseAdviceIntoSections(aiAdvice);

  // かんたん/詳細モードでの表示セクション
  const getDisplaySections = () => {
    if (!simpleMode) return sections;

    // かんたんモード: 「今週のひとつだけ」「安心して食べていいもの」「レシピ（🟢のみ優先）」
    const priorityTypes = ['one_action', 'ok_list', 'recipe'];
    const prioritySections = [];

    priorityTypes.forEach(type => {
      const found = sections.filter(s => s.type === type);
      prioritySections.push(...found);
    });

    // 優先セクションが見つからない場合は最初の3つ
    if (prioritySections.length === 0) return sections.slice(0, 3);

    // かんたんモードでは最大5セクション
    return prioritySections.length > 0 ? prioritySections.slice(0, 5) : sections.slice(0, 3);
  };

  const displaySections = getDisplaySections();

  // 「不足しがちな栄養素」セクションのレンダリング
  const renderDoctorConsultSection = () => {
    if (!doctorConsult || !doctorConsult.supplements || doctorConsult.supplements.length === 0) return null;

    const { supplements } = doctorConsult;

    return (
      <div className="advice-display__block advice-display__block--doctor-consult">
        <div className="advice-display__block-header">
          <div className="advice-display__block-header-content">
            <div className="advice-display__block-info">
              <span className="advice-display__block-icon">🍎</span>
              <h3 className="advice-display__block-title">不足しがちな栄養素</h3>
            </div>
          </div>
        </div>
        <div className="advice-display__block-content">
          <div className="advice-display__content-inner">
            <div className="advice-display__doctor-consult-intro">
              <p>食事から意識してみると良いかもしれません：</p>
            </div>

            <div className="advice-display__doctor-consult-supplements">
              {supplements.slice(0, 3).map((supp, i) => (
                <div key={i} className="advice-display__doctor-consult-item">
                  <strong>{supp.name}</strong>
                  <span className="advice-display__doctor-consult-reason">{supp.reason}</span>
                  <span className="advice-display__doctor-consult-food">食事なら: {supp.foodAlternative}</span>
                </div>
              ))}
            </div>

            <p className="advice-display__doctor-consult-note">
              ※ 栄養素の摂取量や、サプリメントについては主治医にご相談ください。
              お薬を飲んでいる方は、食べるタイミングについても確認すると安心です。
            </p>
          </div>
        </div>
      </div>
    );
  };

  // セクションが検出されない場合
  if (sections.length === 0) {
    return (
      <div className="advice-display">
        <h2 className="advice-display__title">つむぎからのアドバイス</h2>
        {consultationContent?.trim() && (
          <div className="advice-display__block advice-display__block--general">
            <div className="advice-display__block-content">
              <div className="advice-display__content-inner">
                <h3 className="advice-display__block-title">あなたの相談内容</h3>
                <p className="advice-display__consultation-text">{consultationContent}</p>
              </div>
            </div>
          </div>
        )}
        <div className="advice-display__block advice-display__block--general">
          <div className="advice-display__block-content">
            <div className="advice-display__content-inner">
              <div className="advice-display__markdown">
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                  {rawContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
        <div className="advice-display__actions">
          <button onClick={onNewConsultation} className="advice-display__action-btn advice-display__action-btn--new">
            <span>✨</span><span>新しい相談をする</span>
          </button>
          <button onClick={onContinueConsultation} className="advice-display__action-btn advice-display__action-btn--continue">
            <span>💬</span><span>この相談を続ける</span>
          </button>
        </div>
      </div>
    );
  }

  const empathy = getEmpathyMessage();

  return (
    <div className="advice-display">
      {/* 1. 寄り添いメッセージ（フロント固定） */}
      <div className="advice-display__empathy">
        <span className="advice-display__empathy-icon">{empathy.icon}</span>
        <p className="advice-display__empathy-text">{empathy.message}</p>
      </div>

      {/* ヘッダー */}
      <div className="advice-display__header">
        <div className="advice-display__title-row">
          <h2 className="advice-display__title">つむぎからのアドバイス</h2>
        </div>

        {/* 血液検査データ */}
        {labValues && Object.keys(labValues).length > 0 && (() => {
          const validLabKeys = ['TSH', 'FT4', 'FT3', 'HbA1c', '血糖値', 'フェリチン'];
          const filteredLabValues = Object.entries(labValues).filter(([key]) =>
            validLabKeys.includes(key)
          );
          if (filteredLabValues.length === 0) return null;
          return (
            <div className="advice-display__lab-data">
              <h3 className="advice-display__lab-title">
                <span className="advice-display__lab-icon">🩸</span>
                あなたの血液検査データが反映されています
              </h3>
              <div className="advice-display__lab-grid">
                {filteredLabValues.map(([key, value]) => (
                  <div key={key} className="advice-display__lab-item">
                    <div className="advice-display__lab-key">{key}</div>
                    <div className="advice-display__lab-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 表示モード切り替え */}
        <div className="advice-display__mode-toggle">
          <div className="advice-display__mode-intro">
            <p className="advice-display__mode-title">
              {simpleMode ? '😌 大事なところだけ表示しています' : '📚 すべて表示しています'}
            </p>
            <p className="advice-display__mode-subtitle">
              {simpleMode ? '疲れているときは、これだけで大丈夫' : 'じっくり確認したい方向け'}
            </p>
          </div>
          <div className="advice-display__mode-buttons">
            <button
              onClick={() => setSimpleMode(true)}
              className={`advice-display__mode-btn advice-display__mode-btn--simple ${
                simpleMode ? 'advice-display__mode-btn--simple-active' : ''
              }`}
            >
              <span>😌</span><span>かんたん</span>
            </button>
            <button
              onClick={() => setSimpleMode(false)}
              className={`advice-display__mode-btn advice-display__mode-btn--detailed ${
                !simpleMode ? 'advice-display__mode-btn--detailed-active' : ''
              }`}
            >
              <span>📚</span><span>詳細</span>
            </button>
          </div>
        </div>
      </div>

      {/* AI生成セクション */}
      <div className="advice-display__sections">
        {displaySections.map((section, displayIndex) => {
          const isOneAction = section.type === 'one_action';

          return (
            <div
              key={displayIndex}
              className={`advice-display__block advice-display__block--${section.type} ${
                isOneAction ? 'advice-display__block--highlight' : ''
              }`}
            >
              {/* セクションヘッダー */}
              <div className="advice-display__block-header">
                <button
                  onClick={() => toggleSection(displayIndex)}
                  className="advice-display__block-toggle"
                >
                  <div className="advice-display__block-header-content">
                    <div className="advice-display__block-info">
                      <span className="advice-display__block-icon">{section.icon}</span>
                      <h3 className="advice-display__block-title">{section.title}</h3>
                    </div>
                    <svg
                      className={`advice-display__block-chevron ${
                        expandedSections[displayIndex] ? 'advice-display__block-chevron--expanded' : ''
                      }`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* レシピ保存ボタン */}
                {section.type === 'recipe' && (
                  <div className="advice-display__block-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); saveRecipe(displayIndex, section); }}
                      className={`advice-display__save-btn ${
                        savedRecipes.has(displayIndex) ? 'advice-display__save-btn--saved' : ''
                      }`}
                      disabled={savedRecipes.has(displayIndex)}
                    >
                      {savedRecipes.has(displayIndex) ? '✓ 保存済み' : '💾 保存'}
                    </button>
                  </div>
                )}
              </div>

              {/* セクションコンテンツ */}
              {expandedSections[displayIndex] && (
                <div className="advice-display__block-content">
                  <div className="advice-display__content-inner">
                    <div className="advice-display__markdown">
                      <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                        {section.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* かんたんモード時の案内 */}
        {simpleMode && sections.length > displaySections.length && (
          <div className="advice-display__simple-notice">
            <p className="advice-display__simple-notice-title">
              💡 まずはこれだけで大丈夫
            </p>
            <p className="advice-display__simple-notice-text">
              余裕があるときに「詳細」で全ての情報を見られます。
            </p>
          </div>
        )}
      </div>

      {/* 主治医に相談してみること（フロント固定） */}
      {renderDoctorConsultSection()}

      {/* 相談内容 */}
      {consultationContent?.trim() && (
        <div className="advice-display__block advice-display__block--consultation">
          <div className="advice-display__block-header">
            <div className="advice-display__block-header-content">
              <div className="advice-display__block-info">
                <span className="advice-display__block-icon">💭</span>
                <h3 className="advice-display__block-title">あなたの相談内容</h3>
              </div>
            </div>
          </div>
          <div className="advice-display__block-content">
            <div className="advice-display__content-inner">
              <p className="advice-display__consultation-text">{consultationContent}</p>
            </div>
          </div>
        </div>
      )}

      {/* アクションボタン */}
      <div className="advice-display__actions">
        <button onClick={onNewConsultation} className="advice-display__action-btn advice-display__action-btn--new">
          <span>✨</span><span>新しい相談をする</span>
        </button>
        <button onClick={onContinueConsultation} className="advice-display__action-btn advice-display__action-btn--continue">
          <span>💬</span><span>この相談を続ける</span>
        </button>
      </div>

      {/* 医療に関するお知らせ（折りたたみ、末尾に1回のみ） */}
      <details className="advice-display__medical-notice">
        <summary className="advice-display__medical-notice-summary">
          医療に関するお知らせ
        </summary>
        <div className="advice-display__medical-notice-content">
          <p><strong>本サービスは医療行為ではありません。</strong></p>
          <ul>
            <li>提供される情報は一般的な栄養・健康情報であり、個別の医学的判断に代わるものではありません</li>
            <li>本サービスはサプリメントの処方・推奨を行うものではありません</li>
            <li>栄養素の摂取量については、必ず医師または薬剤師にご相談ください</li>
            <li>検査値の解釈、症状の判断、治療方針の決定は必ず医師にご相談ください</li>
            <li>服薬中の方は、食事との相互作用について必ず主治医または薬剤師にご相談ください</li>
          </ul>
        </div>
      </details>
    </div>
  );
};

export default StructuredAdviceDisplay;
